import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Home, Calendar, AlertCircle, DollarSign, Activity, Users, 
  CheckCircle, BarChart3, Building, TrendingUp, Download,
  FileDown, FileSpreadsheet, FileText
} from 'lucide-react';
import { API_BASE_URL } from '../config';

export default function Reports() {
  const [activeReport, setActiveReport] = useState('today-stats');
  const [reportData, setReportData] = useState(null);
  const [dateRange, setDateRange] = useState({
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [loading, setLoading] = useState(false);

  const reports = [
    { id: 'today-stats', name: "Today's Statistics", icon: Home, color: 'blue' },
    { id: 'slot-availability', name: 'Slot Availability', icon: Calendar, color: 'purple' },
    { id: 'service-delays', name: 'Service Delays', icon: AlertCircle, color: 'red' },
    { id: 'top-paid-services', name: 'Top Paid Services', icon: DollarSign, color: 'green' },
    { id: 'top-services-by-count', name: 'Top Services (Count)', icon: Activity, color: 'blue' },
    { id: 'staff-to-guest-ratio', name: 'Staff-to-Guest Ratio', icon: Users, color: 'amber' },
    { id: 'revenue-analysis', name: 'Revenue Analysis', icon: BarChart3, color: 'green' },
    { id: 'service-type-revenue', name: 'Service Type Revenue', icon: TrendingUp, color: 'purple' },
    { id: 'cabin-occupancy', name: 'Cabin Occupancy', icon: Building, color: 'teal' },
    { id: 'therapist-load', name: 'Therapist Load', icon: Users, color: 'blue' },
    { id: 'therapist-performance', name: 'Therapist Performance', icon: Activity, color: 'indigo' },
    { id: 'cabin-load', name: 'Cabin Load', icon: Building, color: 'purple' }
  ];

  useEffect(() => {
    fetchReportData();
  }, [activeReport, dateRange]);

  const fetchReportData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${API_BASE_URL}/reports/${activeReport}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            start_date: dateRange.startDate,
            end_date: dateRange.endDate
          }
        }
      );
      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching report data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${API_BASE_URL}/reports/${activeReport}/export`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            start_date: dateRange.startDate,
            end_date: dateRange.endDate,
            format: format
          },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const extension = format === 'excel' ? 'xlsx' : format === 'pdf' ? 'pdf' : 'csv';
      link.setAttribute('download', `${activeReport}_${dateRange.startDate}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting report:', error);
      alert('Failed to export report. Please try again.');
    }
  };

  const renderReport = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center h-96">
          <div className="text-gray-500">Loading report data...</div>
        </div>
      );
    }

    switch(activeReport) {
      case 'today-stats':
        return <TodayStatistics data={reportData} />;
      case 'slot-availability':
        return <SlotAvailability data={reportData} />;
      case 'service-delays':
        return <ServiceDelays data={reportData} />;
      case 'top-paid-services':
        return <TopPaidServices data={reportData} />;
      case 'top-services-by-count':
        return <TopServicesByCount data={reportData} />;
      case 'staff-to-guest-ratio':
        return <StaffToGuestRatio data={reportData} />;
      case 'revenue-analysis':
        return <RevenueAnalysis data={reportData} />;
      case 'service-type-revenue':
        return <ServiceTypeRevenue data={reportData} />;
      case 'cabin-occupancy':
        return <CabinOccupancy data={reportData} />;
      case 'therapist-load':
        return <TherapistLoad data={reportData} />;
      case 'therapist-performance':
        return <TherapistPerformance data={reportData} />;
      case 'cabin-load':
        return <CabinLoad data={reportData} />;
      default:
        return <TodayStatistics data={reportData} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Left Sidebar Navigation */}
      <div className="w-80 bg-white border-r-2 border-gray-200 flex flex-col">
        {/* Sidebar Header */}
        <div className="px-6 py-6 border-b-2 border-gray-200">
          <h1 className="text-2xl font-bold text-gray-900">Reports & Analytics</h1>
          <p className="text-sm text-gray-500 mt-1">Atmantan Wellness</p>
        </div>

        {/* Navigation Links */}
        <div className="flex-1 overflow-y-auto py-4">
          <div className="space-y-1 px-3">
            {reports.map((report) => {
              const Icon = report.icon;
              const isActive = activeReport === report.id;
              return (
                <button
                  key={report.id}
                  onClick={() => setActiveReport(report.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                    isActive 
                      ? 'bg-[#C7AE6E] text-gray-900 shadow-md' 
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <Icon size={20} className={isActive ? 'text-gray-900' : 'text-gray-400'} />
                  <span className={`font-semibold text-sm ${isActive ? 'text-gray-900' : 'text-gray-700'}`}>
                    {report.name}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Right Content Area */}
      <div className="flex-1 overflow-y-auto flex flex-col">
        {/* Top Bar with Date Filters and Export */}
        <div className="bg-white border-b-2 border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Start Date</label>
                <input
                  type="date"
                  value={dateRange.startDate}
                  onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
                  className="mt-1 block border-2 border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#C7AE6E] focus:border-[#C7AE6E]"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">End Date</label>
                <input
                  type="date"
                  value={dateRange.endDate}
                  onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
                  className="mt-1 block border-2 border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#C7AE6E] focus:border-[#C7AE6E]"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => handleExport('excel')}
                className="flex items-center gap-2 bg-green-50 hover:bg-green-100 text-green-700 font-semibold py-2 px-4 rounded-lg text-sm transition-colors border border-green-200"
              >
                <FileSpreadsheet size={16} />
                Export Excel
              </button>
            </div>
          </div>
        </div>

        {/* Report Content */}
        <div className="flex-1">
          {renderReport()}
        </div>
      </div>
    </div>
  );
}

// Individual Report Components
function TodayStatistics({ data }) {
  // Provide complete default structure
  const defaultStats = {
    guestsInHouse: 0,
    checkInsToday: 0,
    checkOutsToday: 0,
    retreatOccupancyPct: 0,
    avgLengthOfStay: { mtd: 0, ytd: 0 },
    revenueForecast: 0,
    dailyRevenue: 0,
    mtdAvgRevenue: 0,
    genderBooking: { 
      male: { count: 0, percentage: 0 }, 
      female: { count: 0, percentage: 0 } 
    },
    totalServices: 0,
    mtdAvgTreatments: 0,
    therapistLoad: { rostered: 0, avgHours: 0 },
    cabinLoad: { totalCabins: 0, avgHours: 0 }
  };
  
  // Merge data with defaults to ensure all properties exist
  const stats = data ? {
    ...defaultStats,
    ...data,
    avgLengthOfStay: { ...defaultStats.avgLengthOfStay, ...(data.avgLengthOfStay || {}) },
    genderBooking: {
      male: { ...defaultStats.genderBooking.male, ...(data.genderBooking?.male || {}) },
      female: { ...defaultStats.genderBooking.female, ...(data.genderBooking?.female || {}) }
    },
    therapistLoad: { ...defaultStats.therapistLoad, ...(data.therapistLoad || {}) },
    cabinLoad: { ...defaultStats.cabinLoad, ...(data.cabinLoad || {}) }
  } : defaultStats;

  return (
    <div className="p-6 space-y-6">
      <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Today's Statistics</h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Guests In-House" value={stats.guestsInHouse} color="blue" />
          <StatCard label="Check-Ins Today" value={stats.checkInsToday} color="green" />
          <StatCard label="Check-Outs Today" value={stats.checkOutsToday} color="red" />
          <StatCard label="Retreat Occupancy" value={`${stats.retreatOccupancyPct}%`} color="purple" />
        </div>

        <div className="mt-6 border-t-2 border-gray-200 pt-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Average Length of Stay</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 border-2 border-gray-200 p-4 rounded-lg">
              <div className="text-xs text-gray-600 uppercase tracking-wide mb-1">MTD</div>
              <div className="text-3xl font-bold text-gray-900">{stats.avgLengthOfStay.mtd} <span className="text-lg text-gray-500">days</span></div>
            </div>
            <div className="bg-gray-50 border-2 border-gray-200 p-4 rounded-lg">
              <div className="text-xs text-gray-600 uppercase tracking-wide mb-1">YTD</div>
              <div className="text-3xl font-bold text-gray-900">{stats.avgLengthOfStay.ytd} <span className="text-lg text-gray-500">days</span></div>
            </div>
          </div>
        </div>

        <div className="mt-6 border-t-2 border-gray-200 pt-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Revenue</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-blue-50 border-2 border-blue-200 p-4 rounded-lg">
              <div className="text-xs text-gray-600 uppercase tracking-wide mb-1">Revenue Forecast</div>
              <div className="text-2xl font-bold text-blue-600">₹{stats.revenueForecast?.toLocaleString()}</div>
            </div>
            <div className="bg-green-50 border-2 border-green-200 p-4 rounded-lg">
              <div className="text-xs text-gray-600 uppercase tracking-wide mb-1">Daily Revenue</div>
              <div className="text-2xl font-bold text-green-600">₹{stats.dailyRevenue?.toLocaleString()}</div>
            </div>
            <div className="bg-purple-50 border-2 border-purple-200 p-4 rounded-lg">
              <div className="text-xs text-gray-600 uppercase tracking-wide mb-1">MTD Avg Revenue</div>
              <div className="text-2xl font-bold text-purple-600">₹{stats.mtdAvgRevenue?.toLocaleString()}</div>
            </div>
          </div>
        </div>

        <div className="mt-6 border-t-2 border-gray-200 pt-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Gender Booking & Services</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 border-2 border-blue-200 p-4 rounded-lg">
              <div className="text-xs text-gray-600 uppercase tracking-wide mb-1">Male Guests</div>
              <div className="text-2xl font-bold text-blue-600">{stats.genderBooking.male.count}</div>
              <div className="text-sm text-gray-600">{stats.genderBooking.male.percentage}%</div>
            </div>
            <div className="bg-pink-50 border-2 border-pink-200 p-4 rounded-lg">
              <div className="text-xs text-gray-600 uppercase tracking-wide mb-1">Female Guests</div>
              <div className="text-2xl font-bold text-pink-600">{stats.genderBooking.female.count}</div>
              <div className="text-sm text-gray-600">{stats.genderBooking.female.percentage}%</div>
            </div>
            <div className="bg-amber-50 border-2 border-amber-200 p-4 rounded-lg">
              <div className="text-xs text-gray-600 uppercase tracking-wide mb-1">Total Services</div>
              <div className="text-2xl font-bold text-amber-600">{stats.totalServices}</div>
            </div>
            <div className="bg-teal-50 border-2 border-teal-200 p-4 rounded-lg">
              <div className="text-xs text-gray-600 uppercase tracking-wide mb-1">MTD Avg Treatments</div>
              <div className="text-2xl font-bold text-teal-600">{stats.mtdAvgTreatments}</div>
            </div>
          </div>
        </div>

        <div className="mt-6 border-t-2 border-gray-200 pt-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Load Summary (Today)</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gradient-to-r from-blue-50 to-blue-100 border-2 border-blue-300 p-5 rounded-lg">
              <div className="text-xs text-gray-600 uppercase tracking-wide mb-2">Therapist Load</div>
              <div className="flex items-end gap-3">
                <div>
                  <div className="text-xs text-gray-600 mb-1">Rostered Today</div>
                  <div className="text-3xl font-bold text-blue-700">{stats.therapistLoad.rostered}</div>
                </div>
                <div className="flex-1 text-right">
                  <div className="text-xs text-gray-600 mb-1">Avg Hours/Therapist</div>
                  <div className="text-3xl font-bold text-blue-700">{stats.therapistLoad.avgHours}</div>
                </div>
              </div>
            </div>
            
            <div className="bg-gradient-to-r from-purple-50 to-purple-100 border-2 border-purple-300 p-5 rounded-lg">
              <div className="text-xs text-gray-600 uppercase tracking-wide mb-2">Cabin Load</div>
              <div className="flex items-end gap-3">
                <div>
                  <div className="text-xs text-gray-600 mb-1">Total Cabins</div>
                  <div className="text-3xl font-bold text-purple-700">{stats.cabinLoad.totalCabins}</div>
                </div>
                <div className="flex-1 text-right">
                  <div className="text-xs text-gray-600 mb-1">Avg Hours/Cabin</div>
                  <div className="text-3xl font-bold text-purple-700">{stats.cabinLoad.avgHours}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SlotAvailability({ data }) {
  const slots = data?.slots || [];

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Slot Availability by Service Type</h2>
        
        <div className="space-y-4">
          {slots.length > 0 ? slots.map((slot, idx) => (
            <div key={idx} className="border-2 border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <span className="font-bold text-gray-900">{slot.serviceType}</span>
                <span className="text-2xl font-bold text-[#C7AE6E]">{slot.available}</span>
              </div>
              <div className="text-xs text-gray-500 mb-2">Available slots out of {slot.total}</div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className="bg-[#C7AE6E] h-3 rounded-full transition-all"
                  style={{ width: `${slot.total > 0 ? (slot.available / slot.total) * 100 : 0}%` }}
                />
              </div>
              <div className="text-xs text-gray-600 mt-1">
                {slot.total > 0 ? ((slot.available / slot.total) * 100).toFixed(0) : '0'}% available
              </div>
            </div>
          )) : (
            <div className="text-center text-gray-500 py-8">No slot data available</div>
          )}
        </div>
      </div>
    </div>
  );
}

function ServiceDelays({ data }) {
  const delays = data?.delays || [];

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Service Delay Report</h2>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-gray-300">
                <th className="text-left text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Service Type</th>
                <th className="text-center text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Total</th>
                <th className="text-center text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Delayed</th>
                <th className="text-center text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Delay %</th>
                <th className="text-center text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Avg Delay (Min)</th>
              </tr>
            </thead>
            <tbody>
              {delays.length > 0 ? delays.map((delay, idx) => (
                <tr key={idx} className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="py-4 px-4 font-bold text-gray-900">{delay.serviceType}</td>
                  <td className="py-4 px-4 text-center font-semibold text-gray-700">{delay.total}</td>
                  <td className="py-4 px-4 text-center">
                    <span className={`font-bold ${delay.delayed > 0 ? 'text-red-600' : 'text-green-600'}`}>
                      {delay.delayed}
                    </span>
                  </td>
                  <td className="py-4 px-4 text-center">
                    <span className={`inline-block px-3 py-1 rounded-full font-bold text-sm ${
                      delay.delayPercent === 0 ? 'bg-green-100 text-green-700' :
                      delay.delayPercent < 10 ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {delay.delayPercent}%
                    </span>
                  </td>
                  <td className="py-4 px-4 text-center font-semibold text-gray-700">{delay.avgDelayMinutes}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="5" className="py-8 text-center text-gray-500">No delay data available</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function TopPaidServices({ data }) {
  const services = data?.services || [];
  const total = data?.totalWellnessRevenue || 0;

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Top 10 Paid Services</h2>
        <p className="text-gray-600 mb-6">Ranked by revenue contribution</p>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-gray-300">
                <th className="text-left text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-2">Rank</th>
                <th className="text-left text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Service Name</th>
                <th className="text-center text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Count</th>
                <th className="text-right text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Avg Rate</th>
                <th className="text-right text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Revenue</th>
                <th className="text-center text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">% of Total</th>
              </tr>
            </thead>
            <tbody>
              {services.length > 0 ? services.map((service) => (
                <tr key={service.rank} className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="py-4 px-2">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#C7AE6E] bg-opacity-20 text-[#C7AE6E] font-bold text-sm">
                      {service.rank}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <div className="font-bold text-gray-900">{service.name}</div>
                    <div className="text-xs text-gray-500">{service.type}</div>
                  </td>
                  <td className="py-4 px-4 text-center font-bold text-gray-700">{service.count || 0}</td>
                  <td className="py-4 px-4 text-right font-semibold text-gray-700">₹{(service.avgRate || 0).toLocaleString()}</td>
                  <td className="py-4 px-4 text-right font-bold text-green-600">₹{(service.revenue || 0).toLocaleString()}</td>
                  <td className="py-4 px-4 text-center">
                    <span className="inline-block px-2 py-1 bg-green-100 text-green-700 rounded font-bold text-xs">
                      {service.percentOfTotal || 0}%
                    </span>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="6" className="py-8 text-center text-gray-500">No data available</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        <div className="mt-6 pt-6 border-t-2 border-gray-200">
          <div className="flex justify-between items-center">
            <span className="text-lg font-bold text-gray-700">Total Wellness Revenue</span>
            <span className="text-3xl font-bold text-green-600">₹{(total || 0).toLocaleString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function TopServicesByCount({ data }) {
  const services = data?.services || [];
  const total = data?.totalServices || 0;

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Top 10 Services (by Count)</h2>
        <p className="text-gray-600 mb-6">Most frequently booked services</p>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-gray-300">
                <th className="text-left text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-2">Rank</th>
                <th className="text-left text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Service Name</th>
                <th className="text-center text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Count</th>
                <th className="text-center text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">% of Total</th>
              </tr>
            </thead>
            <tbody>
              {services.length > 0 ? services.map((service) => (
                <tr key={service.rank} className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="py-4 px-2">
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#C7AE6E] bg-opacity-20 text-[#C7AE6E] font-bold text-sm">
                      {service.rank}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <div className="font-bold text-gray-900">{service.name}</div>
                    <div className="text-xs text-gray-500">{service.type}</div>
                  </td>
                  <td className="py-4 px-4 text-center font-bold text-gray-900 text-lg">{service.count}</td>
                  <td className="py-4 px-4 text-center">
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div 
                            className="bg-[#C7AE6E] h-2.5 rounded-full"
                            style={{ width: `${service.percentage}%` }}
                          />
                        </div>
                      </div>
                      <span className="font-bold text-gray-900 text-sm w-12">{service.percentage}%</span>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="4" className="py-8 text-center text-gray-500">No data available</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        <div className="mt-6 pt-6 border-t-2 border-gray-200">
          <div className="flex justify-between items-center">
            <span className="text-lg font-bold text-gray-700">Total Services</span>
            <span className="text-3xl font-bold text-gray-900">{total}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function StaffToGuestRatio({ data }) {
  const ratios = data?.ratios || [];
  const guests = data?.guestsInHouse || 0;

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Role-wise Staff to Guest Ratio</h2>
        <p className="text-gray-600 mb-6">Formula: Staff ÷ Guest • Guests In-House: {guests}</p>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-gray-300">
                <th className="text-left text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Role</th>
                <th className="text-center text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Staff Present</th>
                <th className="text-center text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Ratio (Today)</th>
                <th className="text-center text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">MTD Avg Ratio</th>
              </tr>
            </thead>
            <tbody>
              {ratios.length > 0 ? ratios.map((ratio, idx) => (
                <tr key={idx} className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="py-4 px-4 font-bold text-gray-900">{ratio.role}</td>
                  <td className="py-4 px-4 text-center">
                    <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#C7AE6E] bg-opacity-20 text-[#C7AE6E] font-bold">
                      {ratio.staffPresent}
                    </span>
                  </td>
                  <td className="py-4 px-4 text-center font-bold text-gray-900 text-lg">{ratio.ratioToday}</td>
                  <td className="py-4 px-4 text-center font-semibold text-gray-600">{ratio.mtdAvgRatio}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="4" className="py-8 text-center text-gray-500">No data available</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function QualityIndicators({ data }) {
  const indicators = data?.indicators || [];

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Quality Indicators</h2>
        <p className="text-gray-600 mb-6">Service quality metrics with formulas</p>
        
        <div className="space-y-4">
          {indicators.length > 0 ? indicators.map((indicator) => (
            <div key={indicator.slNo} className="border-2 border-gray-200 rounded-lg p-5 hover:border-[#C7AE6E] transition-all">
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#C7AE6E] bg-opacity-20 text-[#C7AE6E] font-bold text-xs">
                      {indicator.slNo}
                    </span>
                    <h3 className="font-bold text-gray-900">{indicator.indicator}</h3>
                  </div>
                  <p className="text-xs text-gray-500 ml-8">{indicator.formula}</p>
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Target</div>
                  <div className="text-lg font-bold text-gray-700">
                    {typeof indicator.target === 'number' ? `${indicator.target}%` : indicator.target}
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 mt-4">
                {/* Today */}
                <div className={`p-3 rounded-lg border-2 ${
                  indicator.todayPct ? 
                    (indicator.todayPct >= indicator.target ? 'bg-green-50 border-green-200' :
                    indicator.todayPct >= indicator.target * 0.9 ? 'bg-yellow-50 border-yellow-200' :
                    'bg-red-50 border-red-200') : 'bg-gray-50 border-gray-200'
                }`}>
                  <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Today</div>
                  <div className={`text-3xl font-bold ${
                    indicator.todayPct ?
                      (indicator.todayPct >= indicator.target ? 'text-green-600' :
                      indicator.todayPct >= indicator.target * 0.9 ? 'text-yellow-600' :
                      'text-red-600') : 'text-gray-600'
                  }`}>
                    {indicator.todayPct ? `${indicator.todayPct}%` : indicator.todayValue}
                  </div>
                </div>
                
                {/* MTD */}
                <div className={`p-3 rounded-lg border-2 ${
                  indicator.mtdPct ?
                    (indicator.mtdPct >= indicator.target ? 'bg-green-50 border-green-200' :
                    indicator.mtdPct >= indicator.target * 0.9 ? 'bg-yellow-50 border-yellow-200' :
                    'bg-red-50 border-red-200') : 'bg-gray-50 border-gray-200'
                }`}>
                  <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">MTD</div>
                  <div className={`text-3xl font-bold ${
                    indicator.mtdPct ?
                      (indicator.mtdPct >= indicator.target ? 'text-green-600' :
                      indicator.mtdPct >= indicator.target * 0.9 ? 'text-yellow-600' :
                      'text-red-600') : 'text-gray-600'
                  }`}>
                    {indicator.mtdPct ? `${indicator.mtdPct}%` : indicator.mtdValue}
                  </div>
                </div>
              </div>
            </div>
          )) : (
            <div className="text-center text-gray-500 py-8">No quality indicators available</div>
          )}
        </div>
      </div>
    </div>
  );
}

function ServiceTypeRevenue({ data }) {
  const breakdown = data?.breakdown || [];
  const total = data?.totalWellnessRevenue || 0;

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Service Type Revenue Breakdown</h2>
        <p className="text-gray-600 mb-6">Revenue analysis by service category</p>
        
        <div className="space-y-3">
          {breakdown.length > 0 ? breakdown.map((item, idx) => (
            <div key={idx} className="border-2 border-gray-200 rounded-lg p-4 hover:border-[#C7AE6E] transition-all">
              <div className="flex justify-between items-center mb-3">
                <div>
                  <div className="font-bold text-gray-900 text-lg">{item.serviceType}</div>
                  <div className="text-xs text-gray-500">{item.count || 0} services • Avg ₹{(item.avgRate || 0).toLocaleString()}</div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-green-600">₹{(item.revenue || 0).toLocaleString()}</div>
                  <div className="text-xs text-gray-600">{item.percentOfTotal || 0}% of total</div>
                </div>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className="bg-[#C7AE6E] h-2.5 rounded-full"
                  style={{ width: `${(item.percentOfTotal || 0) * 2.5}%` }}
                />
              </div>
            </div>
          )) : (
            <div className="text-center text-gray-500 py-8">No revenue data available</div>
          )}
        </div>
        
        <div className="mt-6 pt-6 border-t-2 border-gray-200">
          <div className="flex justify-between items-center">
            <span className="text-lg font-bold text-gray-700">Total Wellness Revenue</span>
            <span className="text-3xl font-bold text-green-600">₹{(total || 0).toLocaleString()}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function TherapistPerformance({ data }) {
  const therapists = data?.therapists || [];

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Therapist Performance</h2>
        <p className="text-gray-600 mb-6">Individual therapist statistics and utilization</p>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-gray-300">
                <th className="text-left text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Therapist</th>
                <th className="text-center text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Services</th>
                <th className="text-center text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Hours</th>
                <th className="text-center text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Utilization</th>
                <th className="text-right text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {therapists.length > 0 ? therapists.map((therapist, idx) => (
                <tr key={idx} className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="py-4 px-4">
                    <div className="font-bold text-gray-900">{therapist.name}</div>
                    <div className="text-xs text-gray-500">{therapist.specialty}</div>
                  </td>
                  <td className="py-4 px-4 text-center font-semibold text-gray-700">{therapist.serviceCount}</td>
                  <td className="py-4 px-4 text-center font-semibold text-gray-700">{therapist.hours}</td>
                  <td className="py-4 px-4 text-center">
                    <span className={`inline-block px-3 py-1 rounded-full font-bold text-sm ${
                      therapist.utilization >= 80 ? 'bg-green-100 text-green-700' :
                      therapist.utilization >= 60 ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {therapist.utilization}%
                    </span>
                  </td>
                  <td className="py-4 px-4 text-right font-bold text-green-600">₹{therapist.revenue?.toLocaleString()}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="5" className="py-8 text-center text-gray-500">No therapist data available</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function GuestStatistics({ data }) {
  const stats = data || {};

  return (
    <div className="p-6 space-y-6">
      <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Guest Statistics</h2>
        
        <div className="grid grid-cols-2 gap-6">
          <StatCard label="Total Active Guests" value={stats.totalActive || 0} color="blue" />
          <StatCard label="Avg Length of Stay" value={`${stats.avgStayDays || 0} days`} color="purple" />
          <StatCard label="New Guests (This Month)" value={stats.newGuestsMonth || 0} color="green" />
          <StatCard label="Returning Guests" value={stats.returningGuests || 0} color="amber" />
        </div>

        <div className="mt-6 border-t-2 border-gray-200 pt-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Guest Satisfaction</h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-green-50 border-2 border-green-200 p-4 rounded-lg">
              <div className="text-xs text-gray-600 uppercase tracking-wide mb-1">Average Rating</div>
              <div className="text-3xl font-bold text-green-600">{stats.avgRating || 0} / 5</div>
            </div>
            <div className="bg-purple-50 border-2 border-purple-200 p-4 rounded-lg">
              <div className="text-xs text-gray-600 uppercase tracking-wide mb-1">Feedback Responses</div>
              <div className="text-3xl font-bold text-purple-600">{stats.feedbackCount || 0}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function RevenueAnalysis({ data }) {
  const revenue = data || {};
  
  // Extract values from the nested structure
  const todayRevenue = revenue.today?.totalWellnessRevenue || 0;
  const mtdRevenue = revenue.mtd?.totalWellnessRevenue || 0;
  const ytdRevenue = revenue.ytd?.totalWellnessRevenue || 0;
  
  const todayUpgrade = revenue.today?.upgradeTreatmentRevenue || 0;
  const mtdUpgrade = revenue.mtd?.upgradeTreatmentRevenue || 0;
  const ytdUpgrade = revenue.ytd?.upgradeTreatmentRevenue || 0;
  
  const todayAvgSpend = revenue.today?.avgGuestSpend || 0;
  const mtdAvgSpend = revenue.mtd?.avgGuestSpend || 0;
  const ytdAvgSpend = revenue.ytd?.avgGuestSpend || 0;

  const dailyTrend = revenue.dailyTrend || [];
  const showTrend = dailyTrend.length > 1;

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Revenue Analysis</h2>
        <p className="text-gray-600 mb-6">Total wellness revenue and upgrade tracking</p>
        
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4">
            <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Today</div>
            <div className="text-3xl font-bold text-green-600">₹{todayRevenue.toLocaleString()}</div>
            <div className="text-xs text-gray-600 mt-2">
              Upgrades: ₹{todayUpgrade.toLocaleString()} • Avg: ₹{todayAvgSpend.toLocaleString()}
            </div>
          </div>
          <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
            <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">This Month (MTD)</div>
            <div className="text-3xl font-bold text-blue-600">₹{mtdRevenue.toLocaleString()}</div>
            <div className="text-xs text-gray-600 mt-2">
              Upgrades: ₹{mtdUpgrade.toLocaleString()} • Avg: ₹{mtdAvgSpend.toLocaleString()}
            </div>
          </div>
          <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4">
            <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">This Year (YTD)</div>
            <div className="text-3xl font-bold text-purple-600">₹{ytdRevenue.toLocaleString()}</div>
            <div className="text-xs text-gray-600 mt-2">
              Upgrades: ₹{ytdUpgrade.toLocaleString()} • Avg: ₹{ytdAvgSpend.toLocaleString()}
            </div>
          </div>
        </div>

        {/* Daily Trend Chart - Only show if more than 1 day */}
        {showTrend && (
          <div className="border-t-2 border-gray-200 pt-6 mb-6">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Revenue Trend</h3>
            <RevenueTrendChart data={dailyTrend} />
          </div>
        )}

        <div className="border-t-2 border-gray-200 pt-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Revenue Details</h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-2">Avg Paid Treatments/Guest</div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span>Today:</span><span className="font-bold">{revenue.today?.avgPaidTreatmentPerGuest || 0}</span></div>
                <div className="flex justify-between"><span>MTD:</span><span className="font-bold">{revenue.mtd?.avgPaidTreatmentPerGuest || 0}</span></div>
                <div className="flex justify-between"><span>YTD:</span><span className="font-bold">{revenue.ytd?.avgPaidTreatmentPerGuest || 0}</span></div>
              </div>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-2">Avg Retreat Treatments/Guest</div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span>Today:</span><span className="font-bold">{revenue.today?.avgRetreatTreatmentPerGuest || 0}</span></div>
                <div className="flex justify-between"><span>MTD:</span><span className="font-bold">{revenue.mtd?.avgRetreatTreatmentPerGuest || 0}</span></div>
                <div className="flex justify-between"><span>YTD:</span><span className="font-bold">{revenue.ytd?.avgRetreatTreatmentPerGuest || 0}</span></div>
              </div>
            </div>
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-2">Guest Spending</div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between"><span>Today:</span><span className="font-bold">₹{todayAvgSpend.toLocaleString()}</span></div>
                <div className="flex justify-between"><span>MTD:</span><span className="font-bold">₹{mtdAvgSpend.toLocaleString()}</span></div>
                <div className="flex justify-between"><span>YTD:</span><span className="font-bold">₹{ytdAvgSpend.toLocaleString()}</span></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Revenue Trend Chart Component
function RevenueTrendChart({ data }) {
  if (!data || data.length === 0) return null;

  // Chart dimensions
  const width = 1000;
  const height = 300;
  const padding = { top: 20, right: 50, bottom: 50, left: 80 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  // Find min/max values
  const allRevenues = data.map(d => d.totalRevenue);
  const maxRevenue = Math.max(...allRevenues, 0);
  const minRevenue = Math.min(...allRevenues, 0);
  const revenueRange = maxRevenue - minRevenue || 1;

  // Scale functions
  const xScale = (index) => (index / (data.length - 1 || 1)) * chartWidth;
  const yScale = (value) => chartHeight - ((value - minRevenue) / revenueRange) * chartHeight;

  // Create path for line
  const linePath = data.map((d, i) => {
    const x = xScale(i);
    const y = yScale(d.totalRevenue);
    return i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
  }).join(' ');

  // Create path for upgrade line
  const upgradeLinePath = data.map((d, i) => {
    const x = xScale(i);
    const y = yScale(d.upgradeRevenue);
    return i === 0 ? `M ${x} ${y}` : `L ${x} ${y}`;
  }).join(' ');

  // Y-axis labels (5 levels)
  const yLabels = [];
  for (let i = 0; i <= 4; i++) {
    const value = minRevenue + (revenueRange * i / 4);
    const y = yScale(value);
    yLabels.push({ value: Math.round(value), y });
  }

  // Format date for x-axis
  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return `${d.getDate()}/${d.getMonth() + 1}`;
  };

  // Sample x-axis labels (show max 10 labels)
  const xLabelInterval = Math.ceil(data.length / 10);
  const xLabels = data.filter((_, i) => i % xLabelInterval === 0 || i === data.length - 1);

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-4">
          <div className="flex items-center">
            <div className="w-4 h-1 bg-blue-500 mr-2"></div>
            <span className="text-sm text-gray-600">Total Revenue</span>
          </div>
          <div className="flex items-center">
            <div className="w-4 h-1 bg-orange-500 mr-2"></div>
            <span className="text-sm text-gray-600">Upgrade Revenue</span>
          </div>
        </div>
      </div>

      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-visible">
        {/* Y-axis grid lines and labels */}
        {yLabels.map((label, i) => (
          <g key={i}>
            <line
              x1={padding.left}
              y1={padding.top + label.y}
              x2={padding.left + chartWidth}
              y2={padding.top + label.y}
              stroke="#e5e7eb"
              strokeWidth="1"
            />
            <text
              x={padding.left - 10}
              y={padding.top + label.y + 4}
              textAnchor="end"
              fontSize="12"
              fill="#6b7280"
            >
              ₹{label.value.toLocaleString()}
            </text>
          </g>
        ))}

        {/* X-axis labels */}
        {xLabels.map((d, i) => {
          const actualIndex = data.indexOf(d);
          const x = xScale(actualIndex);
          return (
            <text
              key={i}
              x={padding.left + x}
              y={padding.top + chartHeight + 20}
              textAnchor="middle"
              fontSize="11"
              fill="#6b7280"
            >
              {formatDate(d.date)}
            </text>
          );
        })}

        {/* Chart lines */}
        <g transform={`translate(${padding.left}, ${padding.top})`}>
          {/* Upgrade revenue line (orange) */}
          <path
            d={upgradeLinePath}
            fill="none"
            stroke="#f97316"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Total revenue line (blue) */}
          <path
            d={linePath}
            fill="none"
            stroke="#3b82f6"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {/* Data points */}
          {data.map((d, i) => {
            const x = xScale(i);
            const y = yScale(d.totalRevenue);
            return (
              <g key={i}>
                <circle
                  cx={x}
                  cy={y}
                  r="4"
                  fill="#3b82f6"
                  stroke="white"
                  strokeWidth="2"
                />
                <title>{`${formatDate(d.date)}: ₹${d.totalRevenue.toLocaleString()}`}</title>
              </g>
            );
          })}

          {/* Upgrade data points */}
          {data.map((d, i) => {
            const x = xScale(i);
            const y = yScale(d.upgradeRevenue);
            return (
              <g key={`upgrade-${i}`}>
                <circle
                  cx={x}
                  cy={y}
                  r="3"
                  fill="#f97316"
                  stroke="white"
                  strokeWidth="2"
                />
                <title>{`${formatDate(d.date)} Upgrades: ₹${d.upgradeRevenue.toLocaleString()}`}</title>
              </g>
            );
          })}
        </g>
      </svg>
    </div>
  );
}

function CabinOccupancy({ data }) {
  const cabins = data?.cabins || [];
  const total = data?.total || {};
  const totalOccupancy = total.occupancyPct || 0;

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Cabin Occupancy</h2>
        <p className="text-gray-600 mb-6">Treatment room utilization and availability</p>
        
        <div className="mb-6 bg-gradient-to-r from-teal-50 to-teal-100 border-2 border-teal-300 rounded-lg p-5">
          <div className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-1">Overall Occupancy</div>
          <div className="text-4xl font-bold text-teal-700">{totalOccupancy}%</div>
          <div className="text-sm text-gray-600 mt-2">
            {total.serviceCount || 0} services • {(total.hoursOccupied || 0).toFixed(1)} hours used
          </div>
        </div>

        <div className="overflow-x-auto border-2 border-gray-200 rounded-lg">
          <table className="w-full">
            <thead>
              <tr className="border-b-2 border-gray-300 bg-gray-50">
                <th className="text-left text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Cabin Type</th>
                <th className="text-center text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Services</th>
                <th className="text-center text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Hours Used</th>
                <th className="text-center text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Occupancy %</th>
              </tr>
            </thead>
            <tbody>
              {cabins.length > 0 ? cabins.map((cabin, idx) => (
                <tr key={idx} className="border-b border-gray-200 hover:bg-gray-50">
                  <td className="py-4 px-4 font-bold text-gray-900">{cabin.cabinType}</td>
                  <td className="py-4 px-4 text-center font-semibold text-gray-700">{cabin.serviceCount || 0}</td>
                  <td className="py-4 px-4 text-center font-bold text-gray-900">{cabin.hoursOccupied || 0}</td>
                  <td className="py-4 px-4 text-center">
                    <div className="flex items-center gap-3 justify-center">
                      <div className="w-32">
                        <div className="w-full bg-gray-200 rounded-full h-2.5">
                          <div 
                            className={`h-2.5 rounded-full ${
                              (cabin.occupancyPct || 0) >= 75 ? 'bg-green-500' :
                              (cabin.occupancyPct || 0) >= 50 ? 'bg-amber-400' :
                              'bg-red-500'
                            }`}
                            style={{ width: `${cabin.occupancyPct || 0}%` }}
                          />
                        </div>
                      </div>
                      <span className={`font-bold ${
                        (cabin.occupancyPct || 0) >= 75 ? 'text-green-600' :
                        (cabin.occupancyPct || 0) >= 50 ? 'text-amber-600' :
                        'text-red-600'
                      }`}>
                        {cabin.occupancyPct || 0}%
                      </span>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="4" className="py-8 text-center text-gray-500">No cabin data available</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function QualityMetrics({ data }) {
  const metrics = data?.metrics || [];

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Quality Metrics</h2>
        <p className="text-gray-600 mb-6">Service quality indicators and compliance tracking</p>
        
        <div className="space-y-4">
          {metrics.length > 0 ? metrics.map((metric, idx) => (
            <div key={idx} className="border-2 border-gray-200 rounded-lg p-5 hover:border-[#C7AE6E] transition-all">
              <div className="flex items-center justify-between mb-3">
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900">{metric.name}</h3>
                  <p className="text-xs text-gray-500 mt-1">{metric.description}</p>
                </div>
                <div className="text-right">
                  <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Target</div>
                  <div className="text-lg font-bold text-gray-700">{metric.target}%</div>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className={`p-3 rounded-lg border-2 ${
                  metric.current >= metric.target ? 'bg-green-50 border-green-200' :
                  metric.current >= metric.target * 0.9 ? 'bg-yellow-50 border-yellow-200' :
                  'bg-red-50 border-red-200'
                }`}>
                  <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Current</div>
                  <div className={`text-3xl font-bold ${
                    metric.current >= metric.target ? 'text-green-600' :
                    metric.current >= metric.target * 0.9 ? 'text-yellow-600' :
                    'text-red-600'
                  }`}>
                    {metric.current}%
                  </div>
                </div>
                <div className="p-3 rounded-lg border-2 bg-gray-50 border-gray-200">
                  <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">Average</div>
                  <div className="text-3xl font-bold text-gray-700">{metric.average}%</div>
                </div>
              </div>
            </div>
          )) : (
            <div className="text-center text-gray-500 py-8">No quality metrics available</div>
          )}
        </div>
      </div>
    </div>
  );
}

function TherapistLoad({ data }) {
  const overall = data?.overall || { totalTherapists: 0, avgAppointments: 0, avgHours: 0 };
  const byRole = data?.byRole || [];
  const therapists = data?.therapists || [];

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Therapist Load Analysis</h2>
        <p className="text-gray-600 mb-6">Average load based on rostered days within selected date range</p>
        
        {/* Overall Section */}
        <div className="mb-6 bg-gradient-to-r from-blue-50 to-blue-100 border-2 border-blue-300 rounded-lg p-5">
          <div className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">Overall Average</div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <div className="text-xs text-gray-600 mb-1">Total Therapists</div>
              <div className="text-3xl font-bold text-blue-700">{overall.totalTherapists}</div>
            </div>
            <div>
              <div className="text-xs text-gray-600 mb-1">Avg Appointments/Day</div>
              <div className="text-3xl font-bold text-blue-700">{overall.avgAppointments?.toFixed(1) || '0.0'}</div>
            </div>
            <div>
              <div className="text-xs text-gray-600 mb-1">Avg Hours/Day</div>
              <div className="text-3xl font-bold text-blue-700">{overall.avgHours?.toFixed(1) || '0.0'}</div>
            </div>
          </div>
        </div>

        {/* By Role Section */}
        <div className="mb-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Load by Role</h3>
          <div className="overflow-x-auto border-2 border-gray-200 rounded-lg">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-300 bg-gray-50">
                  <th className="text-left text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Role</th>
                  <th className="text-center text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Therapists</th>
                  <th className="text-center text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Avg Appointments/Day</th>
                  <th className="text-center text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Avg Hours/Day</th>
                </tr>
              </thead>
              <tbody>
                {byRole.length > 0 ? byRole.map((role, idx) => (
                  <tr key={idx} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="py-4 px-4 font-bold text-gray-900">{role.role}</td>
                    <td className="py-4 px-4 text-center">
                      <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-700 font-bold">
                        {role.therapistCount}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-center font-bold text-blue-600 text-lg">{role.avgAppointments?.toFixed(1) || '0.0'}</td>
                    <td className="py-4 px-4 text-center font-bold text-green-600 text-lg">{role.avgHours?.toFixed(1) || '0.0'}</td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="4" className="py-8 text-center text-gray-500">No role data available</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Individual Therapists */}
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-4">Individual Therapist Load</h3>
          <div className="overflow-x-auto border-2 border-gray-200 rounded-lg">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-300 bg-gray-50">
                  <th className="text-left text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Therapist</th>
                  <th className="text-left text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Role</th>
                  <th className="text-center text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Rostered Days</th>
                  <th className="text-center text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Total Appointments</th>
                  <th className="text-center text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Total Hours</th>
                  <th className="text-center text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Avg/Day</th>
                </tr>
              </thead>
              <tbody>
                {therapists.length > 0 ? therapists.map((therapist, idx) => (
                  <tr key={idx} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="py-4 px-4 font-bold text-gray-900">{therapist.name}</td>
                    <td className="py-4 px-4 text-gray-700">{therapist.role || 'N/A'}</td>
                    <td className="py-4 px-4 text-center font-semibold text-gray-700">{therapist.rosteredDays || 0}</td>
                    <td className="py-4 px-4 text-center font-bold text-blue-600">{therapist.appointmentCount || 0}</td>
                    <td className="py-4 px-4 text-center font-bold text-green-600">{therapist.totalHours?.toFixed(1) || '0.0'}</td>
                    <td className="py-4 px-4 text-center">
                      <span className={`inline-block px-3 py-1 rounded-full font-bold text-sm ${
                        (therapist.avgPerDay || 0) >= 5 ? 'bg-green-100 text-green-700' :
                        (therapist.avgPerDay || 0) >= 3 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {therapist.avgPerDay?.toFixed(1) || '0.0'}
                      </span>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="6" className="py-8 text-center text-gray-500">No therapist data available</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function CabinLoad({ data }) {
  const overall = data?.overall || { totalCabins: 0, avgAppointments: 0, avgHours: 0 };
  const byType = data?.byType || [];
  const cabins = data?.cabins || [];

  return (
    <div className="p-6">
      <div className="bg-white rounded-lg border-2 border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Cabin Load Analysis</h2>
        <p className="text-gray-600 mb-6">Cabin utilization within selected date range</p>
        
        {/* Overall Section */}
        <div className="mb-6 bg-gradient-to-r from-purple-50 to-purple-100 border-2 border-purple-300 rounded-lg p-5">
          <div className="text-sm font-semibold text-gray-600 uppercase tracking-wide mb-3">Overall Average</div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <div className="text-xs text-gray-600 mb-1">Total Cabins</div>
              <div className="text-3xl font-bold text-purple-700">{overall.totalCabins}</div>
            </div>
            <div>
              <div className="text-xs text-gray-600 mb-1">Avg Appointments/Day</div>
              <div className="text-3xl font-bold text-purple-700">{overall.avgAppointments?.toFixed(1) || '0.0'}</div>
            </div>
            <div>
              <div className="text-xs text-gray-600 mb-1">Avg Hours/Day</div>
              <div className="text-3xl font-bold text-purple-700">{overall.avgHours?.toFixed(1) || '0.0'}</div>
            </div>
          </div>
        </div>

        {/* By Type Section */}
        <div className="mb-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Load by Cabin Type</h3>
          <div className="overflow-x-auto border-2 border-gray-200 rounded-lg">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-300 bg-gray-50">
                  <th className="text-left text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Cabin Type</th>
                  <th className="text-center text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Cabins</th>
                  <th className="text-center text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Avg Appointments/Day</th>
                  <th className="text-center text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Avg Hours/Day</th>
                </tr>
              </thead>
              <tbody>
                {byType.length > 0 ? byType.map((type, idx) => (
                  <tr key={idx} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="py-4 px-4 font-bold text-gray-900">{type.cabinType}</td>
                    <td className="py-4 px-4 text-center">
                      <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-purple-100 text-purple-700 font-bold">
                        {type.cabinCount}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-center font-bold text-purple-600 text-lg">{type.avgAppointments?.toFixed(1) || '0.0'}</td>
                    <td className="py-4 px-4 text-center font-bold text-green-600 text-lg">{type.avgHours?.toFixed(1) || '0.0'}</td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="4" className="py-8 text-center text-gray-500">No cabin type data available</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Individual Cabins */}
        <div>
          <h3 className="text-lg font-bold text-gray-900 mb-4">Individual Cabin Load</h3>
          <div className="overflow-x-auto border-2 border-gray-200 rounded-lg">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-300 bg-gray-50">
                  <th className="text-left text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Cabin</th>
                  <th className="text-left text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Type</th>
                  <th className="text-center text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Days in Range</th>
                  <th className="text-center text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Total Appointments</th>
                  <th className="text-center text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Total Hours</th>
                  <th className="text-center text-xs font-bold text-gray-500 uppercase tracking-wide py-3 px-4">Avg/Day</th>
                </tr>
              </thead>
              <tbody>
                {cabins.length > 0 ? cabins.map((cabin, idx) => (
                  <tr key={idx} className="border-b border-gray-200 hover:bg-gray-50">
                    <td className="py-4 px-4 font-bold text-gray-900">{cabin.name}</td>
                    <td className="py-4 px-4 text-gray-700">{cabin.cabinType || 'N/A'}</td>
                    <td className="py-4 px-4 text-center font-semibold text-gray-700">{cabin.daysInRange || 0}</td>
                    <td className="py-4 px-4 text-center font-bold text-purple-600">{cabin.appointmentCount || 0}</td>
                    <td className="py-4 px-4 text-center font-bold text-green-600">{cabin.totalHours?.toFixed(1) || '0.0'}</td>
                    <td className="py-4 px-4 text-center">
                      <span className={`inline-block px-3 py-1 rounded-full font-bold text-sm ${
                        (cabin.avgPerDay || 0) >= 8 ? 'bg-green-100 text-green-700' :
                        (cabin.avgPerDay || 0) >= 5 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {cabin.avgPerDay?.toFixed(1) || '0.0'}
                      </span>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="6" className="py-8 text-center text-gray-500">No cabin data available</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, color }) {
  const colorClasses = {
    blue: 'bg-blue-50 border-blue-200',
    green: 'bg-green-50 border-green-200',
    red: 'bg-red-50 border-red-200',
    purple: 'bg-purple-50 border-purple-200',
    amber: 'bg-amber-50 border-amber-200'
  };

  return (
    <div className={`border-2 rounded-lg p-4 ${colorClasses[color] || colorClasses.blue}`}>
      <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">{label}</div>
      <div className="text-4xl font-bold text-gray-900">{value}</div>
    </div>
  );
}